/**
 * PlausibleTracker — RGPD-friendly page-view tracking via Plausible.
 *
 * Activation : renseigner deux variables d'environnement au moment du
 * build du frontend :
 *   - REACT_APP_PLAUSIBLE_DOMAIN=discoveur.com
 *   - REACT_APP_PLAUSIBLE_HOST=https://plausible.io       (défaut cloud)
 *     OU  REACT_APP_PLAUSIBLE_HOST=https://analytics.tondomaine.com (self-hosted)
 *
 * Sans ces variables, le composant est un no-op complet — aucun script
 * externe n'est chargé, aucune requête n'est envoyée. Cette approche
 * préserve les performances et la vie privée en preview / en dev.
 *
 * Le tracker officiel Plausible :
 *   - n'utilise pas de cookies (100 % RGPD, aucune bannière de consentement)
 *   - ne collecte pas d'IP ni d'identifiant persistant
 *   - complète le tracker custom `analytics.js` (côté serveur) sans le remplacer
 *
 * Événements de conversion custom : appeler `plausibleEvent("subscribed")`
 * (ou tout autre nom) depuis n'importe quel composant après une action clé.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const DOMAIN = process.env.REACT_APP_PLAUSIBLE_DOMAIN || "";
const HOST = (process.env.REACT_APP_PLAUSIBLE_HOST || "https://plausible.io").replace(/\/$/, "");

// Load the Plausible script exactly once, only when configured.
let scriptLoaded = false;
function ensureScript() {
    if (scriptLoaded || !DOMAIN || typeof document === "undefined") return;
    const s = document.createElement("script");
    s.defer = true;
    s.src = `${HOST}/js/script.js`;
    s.setAttribute("data-domain", DOMAIN);
    document.head.appendChild(s);
    // Provide the queue helper so plausibleEvent() calls made before
    // the script has finished loading are still delivered.
    window.plausible = window.plausible || function () {
        (window.plausible.q = window.plausible.q || []).push(arguments);
    };
    scriptLoaded = true;
}

export default function PlausibleTracker() {
    const location = useLocation();

    // Bootstrap the script on first mount.
    useEffect(() => { ensureScript(); }, []);

    // Fire a pageview on every route change (SPA nav). Plausible's
    // official script only auto-tracks the FIRST load; we rebroadcast
    // pageviews on client navigation.
    useEffect(() => {
        if (!DOMAIN || !window.plausible) return;
        window.plausible("pageview");
    }, [location.pathname, location.search]);

    return null;
}

/**
 * plausibleEvent("subscribed", { plan: "premium" })
 *
 * Fires a Plausible custom event if the tracker is configured; no-op
 * otherwise. Meta object is passed as `props` (Plausible's terminology).
 */
export function plausibleEvent(name, meta) {
    if (!DOMAIN || typeof window === "undefined") return;
    if (typeof window.plausible !== "function") return;
    try {
        window.plausible(name, meta ? { props: meta } : undefined);
    } catch {
        /* best-effort */
    }
}
